import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
} from "ag-grid-community";
import { apiGet, apiPut } from "../../services/genesisApi/httpsMethods";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import type { LoanComp } from "../../types/deal";
import { HyperLinkRenderer } from "../../utils/hyperlinkRenderer";
import {
  numberFormatter,
  dateFormatter,
  normaliseToDateInput,
} from "../../utils/numberFormatter";
import "../../assets/css/Text-General.css";
import { useCurrentUser } from "@/context/CurrentUserContext";
import "../../assets/css/AGGrid-custom.css";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

const ProjectLinkRenderer = HyperLinkRenderer("loan-comps");

const LoanComps = () => {
  const { canEdit } = useCurrentUser();
  const [editMode, setEditMode] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [filter, setFilter] = useState("");
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [newLoanComps, setNewLoanComps] = useState<LoanComp[]>([]);
  const [initialLoanComps, setInitialLoanComps] = useState<LoanComp[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    const fetchLoanComps = async () => {
      try {
        const response = await apiGet("loan-comps");

        if (response.success && response.data) {
          setNewLoanComps(response.data);
          setInitialLoanComps(structuredClone(response.data)); // Create a deep copy for initial state
        } else {
          console.error("Failed to fetch loan comps:", response.message);
        }
      } catch (error) {
        console.error("Failed to fetch loan comps", error);
      }
    };

    fetchLoanComps();
  }, []);

  useEffect(() => {
    const isEqual = areRecordsEqual(initialLoanComps, newLoanComps);
    setDirtyGrid(!isEqual);
  }, [initialLoanComps, newLoanComps]);

  const onCellValueChanged = useCallback((event: any) => {
    const { newValue, oldValue, data } = event;
    if (newValue === oldValue) return;
    setNewLoanComps((prevLoanComps: LoanComp[]) =>
      prevLoanComps.map((loanComp) =>
        loanComp.id === data.id ? { ...data } : loanComp,
      ),
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    // Clone so AG Grid edits don't mutate the baseline rows.
    setNewLoanComps(structuredClone(initialLoanComps)); // Reset to original
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    if (isSaving) return;
    const changedItems = getChangedRecords(newLoanComps, initialLoanComps);
    setIsSaving(true);
    const updates = changedItems.map((loanComp) => {
      return apiPut("loan-comp", loanComp.id, loanComp).catch(
        (error): ApiResponse => {
          console.error(`Failed to update loan comp ${loanComp.id}`, error);
          return {
            success: false,
            status: 0,
            message: "Failed to save loan comp changes. Please try again.",
          };
        },
      );
    });
    const results = await Promise.all(updates);
    const failedResponse = results.find((r) => !r?.success);
    if (failedResponse) {
      // Stay in edit mode and keep the user's edits so they can retry.
      setIsSaving(false);
      showErrorFromResponse(failedResponse);
      return;
    }
    window.location.reload();
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const filteredRows = useMemo(() => {
    return newLoanComps?.filter((s) =>
      (s.project_name ?? "").toLowerCase().includes(filter.toLowerCase()),
    );
  }, [newLoanComps, filter]);

  // Country / sector are display-only here: the save handler PUTs the whole row and
  // the backend update ignores the nested `country` / `sector` objects.
  const columnDefs: ColDef<LoanComp>[] = useMemo(
    () => [
      {
        field: "project_name",
        headerName: "Project Name",
        editable: editMode,
        width: 180,
        // Many Market Tracker rows have no project name; keep them linkable.
        cellRenderer: (params: ICellRendererParams<LoanComp>) =>
          ProjectLinkRenderer({
            ...params,
            value: params.value || `Loan comp #${params.data?.id ?? ""}`,
          }),
      },
      {
        field: "loan_type",
        headerName: "Loan Type",
        editable: editMode,
        width: 150,
      },
      {
        field: "date",
        headerName: "Date",
        editable: editMode,
        width: 120,
        cellEditor: "agDateStringCellEditor",
        valueParser: (params) => normaliseToDateInput(params.newValue),
        valueFormatter: dateFormatter,
      },
      {
        field: "sponsor",
        headerName: "Sponsor",
        editable: editMode,
        width: 150,
      },
      {
        colId: "country",
        headerName: "Country",
        editable: false,
        width: 130,
        valueGetter: (params) => params.data?.country?.nicename ?? null,
      },
      { field: "city", headerName: "City", editable: editMode, width: 120 },
      {
        colId: "sector",
        headerName: "Sector",
        editable: false,
        width: 130,
        valueGetter: (params) => params.data?.sector?.sector ?? null,
      },
      {
        field: "lender_type",
        headerName: "Lender Type",
        editable: editMode,
        width: 130,
      },
      {
        field: "loan_amount_local",
        headerName: "Loan Amount (Local)",
        editable: editMode,
        width: 150,
        cellEditor: "agNumberCellEditor",
        valueFormatter: numberFormatter,
      },
      {
        field: "ltc_ltv",
        headerName: "LTC/LTV (%)",
        editable: editMode,
        width: 110,
        cellEditor: "agNumberCellEditor",
        valueFormatter: numberFormatter,
      },
      {
        field: "margin",
        headerName: "Margin (%)",
        editable: editMode,
        width: 110,
        cellEditor: "agNumberCellEditor",
        valueFormatter: numberFormatter,
      },
      {
        field: "irr",
        headerName: "IRR (%)",
        editable: editMode,
        width: 100,
        cellEditor: "agNumberCellEditor",
        valueFormatter: numberFormatter,
      },
      {
        field: "est_local_irr",
        headerName: "Est. Local IRR (%)",
        editable: editMode,
        width: 140,
        cellEditor: "agNumberCellEditor",
        valueFormatter: numberFormatter,
      },
      {
        field: "comment",
        headerName: "Comment",
        editable: editMode,
        width: 250,
        tooltipField: "comment",
      },
    ],
    [editMode],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <SimpleToolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              minWidth: 0,
            }}
          >
            <CustomTextInput
              label="Project name"
              value={filter}
              onChange={(val) => typeof val === "string" && setFilter(val)}
            />
          </Box>
          <Box
            data-position="right"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "15px",
            }}
          >
            {editMode && (
              <>
                <CustomButton
                  text="Cancel"
                  handleButtonPress={handleCancelButtonPress}
                  isButtonDisabled={isSaving}
                  solid={false}
                  widthOverride="100px"
                />
                <CustomButton
                  text="Save"
                  handleButtonPress={requestSave}
                  isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
                  isLoading={isSaving}
                  isLoadingText="Saving"
                  solid
                  widthOverride="100px"
                />
              </>
            )}
            {!editMode && (
              <CustomButton
                text="Export to CSV"
                handleButtonPress={() =>
                  gridRef.current?.api.exportDataAsCsv({ fileName: "loan-comps" })
                }
                isButtonDisabled={false}
                solid={false}
                widthOverride="100px"
              />
            )}
            <CustomButton
              text="Edit"
              handleButtonPress={() => {
                if (!canEdit) return;
                setEditMode(true);
              }}
              isButtonDisabled={!canEdit || editMode}
              solid={false}
              widthOverride="100px"
            />
          </Box>
        </SimpleToolbar>
      </Box>

      {/* AG Grid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            {...gridEditProps}
            rowData={filteredRows}
            ref={gridRef}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit={true}
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default LoanComps;
